import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { differenceInCalendarDays, parseISO } from "date-fns";
import { Banknote, CreditCard, Percent, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import FinanceBreakdownCard, {
  buildSettlementBreakdownRows,
} from "./FinanceBreakdownCard";
import FinanceKpiCard from "./FinanceKpiCard";
import SettlementTrendChart from "./SettlementTrendChart";
import DataTable from "../../common/DataTable/DataTable";
import ExportDateRangeModal from "../../common/ExportDateRangeModal/ExportDateRangeModal";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import documentExportIcon from "../../resources/icons/document-export-blue.svg";
import { GET } from "../../service/CRUDService";
import { buildQueryString } from "../../utils/buildQueryString";
import {
  classifyApiError,
  getApiErrorCopy,
} from "../../utils/classifyApiError";
import {
  downloadBlob,
  filenameFromContentDisposition,
} from "../../utils/downloadBlob";
import { formatCount, formatPaisaToRupees } from "../../utils/formatCurrency";
import { formatDate } from "../../utils/formatDate";
import { parseSuccessEnvelope } from "../../utils/parseSuccessEnvelope";

const PAGE_LIMIT = 10;

const MAX_LABELLED_TREND_DAYS = 30;

const isTrendRangeLabelled = (dateParams) => {
  const start = dateParams?.start_date;
  const end = dateParams?.end_date;

  if (!start || !end) return false;

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return true;
  }

  return (
    differenceInCalendarDays(endDate, startDate) + 1 <= MAX_LABELLED_TREND_DAYS
  );
};

const PAYMENT_STATUS_CONFIG = {
  Done: {
    dotColor: "var(--issued-credential-template-card-status-done-bg)",
    bgColor: "var(--cf-active-bg-color)",
    textColor: "var(--cf-active-text-color)",
    label: "finance:payment_status_done",
  },
  "Not Required": {
    dotColor: "var(--issued-screen-tab-button-selected-text-color)",
    bgColor: "var(--issued-screen-tab-button-selected-color)",
    textColor: "var(--issued-screen-tab-button-selected-text-color)",
    label: "finance:payment_status_not_required",
  },
  Pending: {
    dotColor: "var(--cf-warning-border-color)",
    bgColor: "var(--email-warning-container-bg)",
    textColor: "var(--cf-warning-text-color)",
    label: "finance:payment_status_pending",
  },
  Failed: {
    dotColor: "var(--cf-error-text-color)",
    bgColor: "var(--cf-error-bg-color)",
    textColor: "var(--cf-error-text-color)",
    label: "finance:payment_status_failed",
  },
};

const renderPaymentStatus = (value, t) => {
  if (!value) return t("workspace:not_applicable");

  const config = PAYMENT_STATUS_CONFIG[value];
  if (!config) return value;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: config.bgColor, color: config.textColor }}
    >
      <span
        className="cf-status-dot"
        style={{ backgroundColor: config.dotColor }}
      />
      {t(config.label)}
    </span>
  );
};

const VerificationSettlementTab = ({ dateParams }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [summary, setSummary] = useState(null);
  const [trendPoints, setTrendPoints] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [reportCount, setReportCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState({
    summary: true,
    trend: true,
    report: true,
  });
  const [summaryError, setSummaryError] = useState(null);
  const [trendError, setTrendError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportRangeModalOpen, setIsExportRangeModalOpen] = useState(false);

  const isMountedRef = useRef(true);
  const summaryRequestId = useRef(0);
  const trendRequestId = useRef(0);
  const reportRequestId = useRef(0);
  const exportRequestId = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    exportRequestId.current += 1;
    setIsExporting(false);
    setIsExportRangeModalOpen(false);
  }, [dateParams]);

  const isAllTimeRange = !dateParams?.start_date && !dateParams?.end_date;

  const showTrendDateLabels = useMemo(
    () => isTrendRangeLabelled(dateParams),
    [dateParams],
  );

  const fetchSummary = useCallback(async () => {
    const requestId = ++summaryRequestId.current;
    setLoading((prev) => ({ ...prev, summary: true }));
    setSummaryError(null);
    try {
      const response = await GET(
        `${EntityConstants.FINANCE_SETTLEMENT_SUMMARY}${buildQueryString(dateParams)}`,
      );
      const data = await parseSuccessEnvelope(response);
      if (!isMountedRef.current || requestId !== summaryRequestId.current) {
        return;
      }
      setSummary(data);
    } catch (error) {
      if (!isMountedRef.current || requestId !== summaryRequestId.current) {
        return;
      }
      setSummary(null);
      setSummaryError(classifyApiError(error));
    } finally {
      if (isMountedRef.current && requestId === summaryRequestId.current) {
        setLoading((prev) => ({ ...prev, summary: false }));
      }
    }
  }, [dateParams]);

  const fetchTrend = useCallback(async () => {
    const requestId = ++trendRequestId.current;
    setLoading((prev) => ({ ...prev, trend: true }));
    setTrendError(null);
    try {
      const response = await GET(
        `${EntityConstants.FINANCE_SETTLEMENT_TREND}${buildQueryString(dateParams)}`,
      );
      const data = await parseSuccessEnvelope(response);
      if (!isMountedRef.current || requestId !== trendRequestId.current) {
        return;
      }
      setTrendPoints(data?.points || []);
    } catch (error) {
      if (!isMountedRef.current || requestId !== trendRequestId.current) {
        return;
      }
      setTrendError(classifyApiError(error));
    } finally {
      if (isMountedRef.current && requestId === trendRequestId.current) {
        setLoading((prev) => ({ ...prev, trend: false }));
      }
    }
  }, [dateParams]);

  const fetchReport = useCallback(
    async (currentOffset) => {
      const requestId = ++reportRequestId.current;
      setLoading((prev) => ({ ...prev, report: true }));
      setReportError(null);
      try {
        const response = await GET(
          `${EntityConstants.FINANCE_SETTLEMENT_REPORT}${buildQueryString({
            ...dateParams,
            limit: PAGE_LIMIT,
            offset: currentOffset,
          })}`,
        );
        const data = await parseSuccessEnvelope(response);
        if (!isMountedRef.current || requestId !== reportRequestId.current) {
          return;
        }
        setReportRows(data?.results || []);
        setReportCount(Number(data?.count) || 0);
        setOffset(currentOffset);
      } catch (error) {
        if (!isMountedRef.current || requestId !== reportRequestId.current) {
          return;
        }
        setReportError(classifyApiError(error));
      } finally {
        if (isMountedRef.current && requestId === reportRequestId.current) {
          setLoading((prev) => ({ ...prev, report: false }));
        }
      }
    },
    [dateParams],
  );

  const retryAll = useCallback(() => {
    fetchSummary();
    fetchTrend();
    fetchReport(0);
  }, [fetchSummary, fetchTrend, fetchReport]);

  useEffect(() => {
    fetchSummary();
    fetchTrend();
    fetchReport(0);
  }, [fetchSummary, fetchTrend, fetchReport]);

  const hasPrevious = offset > 0;
  const hasNext = offset + reportRows.length < reportCount;

  const handlePrevious = () => {
    if (!hasPrevious) return;
    fetchReport(Math.max(0, offset - PAGE_LIMIT));
  };

  const handleNext = () => {
    if (!hasNext) return;
    fetchReport(offset + PAGE_LIMIT);
  };

  const retryTrend = useCallback(() => {
    fetchTrend();
  }, [fetchTrend]);

  const retryReport = useCallback(() => {
    fetchReport(offset);
  }, [fetchReport, offset]);

  const runExport = useCallback(
    async (exportDateParams) => {
      if (isExporting) return;

      const requestId = ++exportRequestId.current;
      setIsExporting(true);
      try {
        const response = await GET(
          `${EntityConstants.FINANCE_SETTLEMENT_REPORT_EXPORT}${buildQueryString(
            {
              ...exportDateParams,
              format: "xlsx",
            },
          )}`,
        );

        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }

        if (!response) {
          throw new Error(tRef.current("finance:export_failed"));
        }

        if (!response.ok) {
          let message = tRef.current("finance:export_failed");
          try {
            const payload = await response.json();
            message = payload?.message || payload?.error || message;
          } catch {
            // non-JSON error body
          }
          throw new Error(message);
        }

        const blob = await response.blob();
        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }

        const filename = filenameFromContentDisposition(
          response.headers.get("Content-Disposition"),
          "verification-settlement.xlsx",
        );
        downloadBlob(blob, filename);

        Toast({
          type: "success",
          title: tRef.current("finance:export_success"),
          message: tRef.current("finance:download_excel"),
        });

        if (isMountedRef.current) {
          setIsExportRangeModalOpen(false);
        }
      } catch (error) {
        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }
        Toast({
          type: "error",
          title: tRef.current("finance:export_failed"),
          message: error?.message || tRef.current("finance:export_failed"),
        });
      } finally {
        if (isMountedRef.current && requestId === exportRequestId.current) {
          setIsExporting(false);
        }
      }
    },
    [isExporting],
  );

  const handleExport = useCallback(() => {
    if (isExporting) return;

    if (isAllTimeRange) {
      setIsExportRangeModalOpen(true);
      return;
    }

    runExport(dateParams);
  }, [dateParams, isAllTimeRange, isExporting, runExport]);

  const breakdown = useMemo(
    () => buildSettlementBreakdownRows(summary, t),
    [summary, t],
  );

  const columns = useMemo(
    () => [
      {
        key: "transaction_id",
        label: t("finance:transaction_id"),
      },
      {
        key: "student_name",
        label: t("finance:student_name"),
      },
      {
        key: "verifier_org_name",
        label: t("finance:verifier_organisation"),
      },
      {
        key: "payment_status",
        label: t("finance:payment_status"),
        render: (value) => renderPaymentStatus(value, t),
      },
      {
        key: "payment_completed_at",
        label: t("finance:payment_completed_at"),
        render: (value) => formatDate(value, t("workspace:not_applicable")),
      },
      {
        key: "gst_amount",
        label: t("finance:gst"),
        render: (value) => formatPaisaToRupees(value, { fractionDigits: 2 }),
      },
      {
        key: "total_amount",
        label: t("finance:total_amount"),
        render: (value) => formatPaisaToRupees(value, { fractionDigits: 2 }),
      },
    ],
    [t],
  );

  const exportDisabled =
    isExporting || reportCount === 0 || loading.report || !!reportError;

  const gstSubtitle =
    summary?.gst_percentage != null
      ? t("finance:gst_subtitle", { percent: summary.gst_percentage })
      : undefined;

  if (!loading.summary && summaryError) {
    const errorCopy = getApiErrorCopy(summaryError, t);
    return (
      <PageErrorState
        variant="section"
        kind={summaryError.kind}
        title={errorCopy.title}
        description={
          errorCopy.description || t("finance:section_load_error_description")
        }
        primaryLabel={t("finance:try_again")}
        onPrimary={retryAll}
        isRetrying={loading.summary}
        details={
          summaryError.status
            ? t("finance:error_details_status", {
                status: summaryError.status,
              })
            : null
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <FinanceKpiCard
          icon={ShieldCheck}
          iconBgClass="bg-cfTemplateTransitionColor2 text-cfPrimary"
          value={formatCount(summary?.total_verifications)}
          label={t("finance:total_verifications")}
          loading={loading.summary}
        />
        <FinanceKpiCard
          icon={Banknote}
          iconBgClass="bg-cfInfoBg text-cfInfoText"
          value={formatPaisaToRupees(summary?.verification_fee_total)}
          label={t("finance:verification_fees")}
          subtitle={t("finance:verification_fees_subtitle")}
          loading={loading.summary}
        />
        <FinanceKpiCard
          icon={Percent}
          iconBgClass="bg-cfErrorBg text-cfErrorText"
          value={formatPaisaToRupees(summary?.gst_amount)}
          label={t("finance:gst")}
          subtitle={gstSubtitle}
          loading={loading.summary}
        />
        <FinanceKpiCard
          icon={CreditCard}
          iconBgClass="bg-cfActiveBg text-cfIssuedCredentialTemplateCardStatusDoneBG"
          value={formatPaisaToRupees(summary?.amount_owed)}
          label={t("finance:amount_owed")}
          subtitle={t("finance:amount_owed_subtitle")}
          loading={loading.summary}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          {trendError && !loading.trend ? (
            <PageErrorState
              variant="section"
              kind={trendError.kind}
              title={getApiErrorCopy(trendError, t).title}
              description={
                getApiErrorCopy(trendError, t).description ||
                t("finance:section_load_error_description")
              }
              primaryLabel={t("finance:try_again")}
              onPrimary={retryTrend}
              isRetrying={loading.trend}
              details={
                trendError.status
                  ? t("finance:error_details_status", {
                      status: trendError.status,
                    })
                  : null
              }
            />
          ) : (
            <SettlementTrendChart
              points={trendPoints}
              loading={loading.trend}
              showDateLabels={showTrendDateLabels}
            />
          )}
        </div>
        <FinanceBreakdownCard
          title={t("finance:settlement_summary_title")}
          subtitle={t("finance:settlement_summary_subtitle")}
          rows={breakdown.rows}
          totalValue={breakdown.totalValue}
          loading={loading.summary}
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {t("finance:verification_report")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("finance:showing_reports", {
                shown: reportRows.length,
                total: reportCount,
              })}
            </p>
          </div>
          <button
            type="button"
            disabled={exportDisabled}
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cfPrimaryButton hover:bg-cfPrimaryDark rounded-[8px] disabled:bg-cfDisabled disabled:cursor-not-allowed transition-colors"
          >
            {isExporting ? (
              <SmallCFLoader text={t("finance:exporting")} />
            ) : (
              <>
                <img
                  src={documentExportIcon}
                  alt=""
                  className="w-4 h-4 brightness-0 invert"
                />
                {t("finance:download_excel")}
              </>
            )}
          </button>
        </div>
        {reportError && !loading.report ? (
          <PageErrorState
            variant="section"
            kind={reportError.kind}
            title={getApiErrorCopy(reportError, t).title}
            description={
              getApiErrorCopy(reportError, t).description ||
              t("finance:section_load_error_description")
            }
            primaryLabel={t("finance:try_again")}
            onPrimary={retryReport}
            isRetrying={loading.report}
            details={
              reportError.status
                ? t("finance:error_details_status", {
                    status: reportError.status,
                  })
                : null
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={reportRows}
            loading={loading.report}
            emptyMessage={t("finance:no_report_rows")}
            rowKey="transaction_id"
            align="center"
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}
      </div>

      <ExportDateRangeModal
        isOpen={isExportRangeModalOpen}
        onClose={() => setIsExportRangeModalOpen(false)}
        onConfirm={runExport}
        isExporting={isExporting}
        testId="verification-settlement-export-range-modal"
      />
    </div>
  );
};

export default VerificationSettlementTab;
